import { Injectable } from '@angular/core';
import { PopulationService } from './population-menu/population.service';
import { TargetAudienceService } from './target-audience/target-audience.service';
import type { MapboxGeoJSONFeature } from 'mapbox-gl';
import { IncomeDistributionService } from './wealth-menu/income-distribution.service';
import { HomeownershipService } from './homeownership/homeownership.service';
import { EducationService } from './education/education.service';
import { HealthService } from './health/health.service';
import { MapColoringService } from '../../../mapbox/visualization/map-coloring.service';
import {
  AGE_SEX_DISTRIBUTION_CHART,
  AIR_POLLUTION,
  AIR_QUALITY_DISTRIBUTION_CHART,
  availableInBulkFeatures,
  AVERAGE_ANNUAL_SPENDING,
  AVERAGE_HOUSEHOLD_SIZE,
  AVERAGE_TRAVEL_TIME_TO_WORK,
  BACHELORS_DEGREE_OR_HIGHER_FRACTION,
  BEAUTY,
  BEAUTY_POI_CHART,
  COMBINED_HWW_CONST,
  COMPETITORS_POI_CHART,
  CONVENIENCE_POI_CHART,
  CONVENIENCE_STORES,
  CRIME_RATE,
  CUMULATIVE_TRAFFIC_INTENSITY,
  DRUGSTORES,
  EDUCATION_ATTAINMENT_CHART,
  EMPLOYMENT_CHART,
  GENDER_PAY_GAP,
  GROSS_RENT_DISTRIBUTION_CHART,
  HEALTH_INDEX_CONST,
  HEALTHCARE_POI_CHART,
  HOME_OWNERSHIP_RATIO,
  HOME_PRICE_TO_INCOME_RATIO,
  HOUSE_OF_REPRESENTATIVES_ELECTION_RESULTS,
  HOUSEHOLDS,
  HOUSEHOLDS_BY_VEHICLES_CHART,
  INCOME_DISTRIBUTION_CHART,
  INCOME_MEDIANS_BY_YEARS_CHART,
  LIFE_EXPECTANCY,
  MARITAL_STATUS_CHART,
  MEDIAN_AGE,
  MEDIAN_GROSS_RENT,
  MEDIAN_GROSS_RENT_TREND,
  MEDIAN_HOUSEHOLD_INCOME_TREND,
  MEDIAN_HOUSING_SALE_COST,
  MEDIAN_HOUSING_SALE_COST_TREND,
  MEDIAN_INCOME,
  MEDIAN_NUMBER_OF_ROOMS,
  MEDIAN_TENURE,
  NUMBER_OF_ROOMS_CHART,
  NUMBER_OF_VEHICLES_AVAILABLE,
  PATIENT_TO_DENTIST_RATIO,
  PATIENT_TO_PHYSICIAN_RATIO,
  PATIENT_TO_THERAPIST_RATIO,
  PEOPLE_PER_MI2,
  POPULATION,
  POPULATION_BY_YEARS_CHART,
  POPULATION_GROWTH_TREND,
  PRESIDENTIAL_ELECTIONS_RESULTS2020,
  PRESIDENTIAL_ELECTIONS_RESULTS2024,
  PROPERTY_CRIME_RATE,
  RACE_DISTRIBUTION_CHART,
  RETAIL,
  ROAD_DENSITY,
  ROAD_DENSITY_CHART,
  SCHOOLS_POI,
  SENATE_ELECTION_2022_RESULTS,
  SPECIAL_HEALTH_CARE_NEEDS_INDEX,
  STRUCTURE_BUILT_MEDIAN_YEAR,
  TARGET_AUDIENCE,
  targetAudienceIndustries,
  TIME_OF_DEPARTURE_TO_WORK_CHART,
  TRAVEL_TIME_TO_WORK_CHART,
  UNEMPLOYMENT_RATIO,
  UNHEALTHY_BEHAVIOUR_INDEX,
  UNINSURED_RATIO,
  VACANCY_CHART,
  VIOLENT_CRIME_RATE,
  voteDataFeatures,
  WEALTH_INDEX_CONST,
  WISE_INDEX_CONST,
  WORK_AT_HOME,
  WORK_COMMUTE_MODES_CHART,
} from '../../../../shared/types/feature-data-type';
import {
  LayerStoreService,
  ORI_LAYER,
} from '../../../mapbox/services/layer-store.service';
import { PoisService } from './pois/pois.service';
import {
  COMMUTE_AND_MOBILITY_GROUP_TITLE,
  EDUCATION_GROUP_TITLE,
  HEALTH_GROUP_TITLE,
  HOUSING_GROUP_TITLE,
  LayersMenuService,
  POI_GROUP_TITLE,
  POLITICAL_LANDSCAPE_GROUP_TITLE,
  POPULATION_GROUP_TITLE,
  TARGET_AUDIENCE_GROUP_TITLE,
  WEALTH_GROUP_TITLE,
} from './layers-menu.service';
import { PoliticalLandscapeService } from './political-landscape/political-landscape.service';
import { FiltersMenuService } from './filters-menu/filters-menu.service';
import { CommuteAndMobilityService } from './commute-and-mobility/commute-and-mobility.service';
import { FormControl } from '@angular/forms';
import { SubscriptionToLinkedToggles } from './menu-group/menu-group.component';
import { SubscriptionPlans } from '../../../../user/user/user.model';

@Injectable({
  providedIn: 'root',
})
export class LayersDataService {
  public toggleMap: Map<string, FormControl> = new Map<
    string,
    FormControl<boolean>
  >();

  public placesToggleMap: Map<string, FormControl> = new Map<
    string,
    FormControl<boolean>
  >();

  private readonly all = {
    [POPULATION]: { charts: [RACE_DISTRIBUTION_CHART], places: [] },
    [HOUSEHOLDS]: { charts: [MARITAL_STATUS_CHART], places: [] },
    [MEDIAN_AGE]: { charts: [AGE_SEX_DISTRIBUTION_CHART], places: [] },
    [AVERAGE_HOUSEHOLD_SIZE]: {
      charts: [],
      places: [],
    },
    [MEDIAN_INCOME]: { charts: [INCOME_DISTRIBUTION_CHART], places: [] },
    [MEDIAN_HOUSEHOLD_INCOME_TREND]: {
      charts: [INCOME_DISTRIBUTION_CHART, INCOME_MEDIANS_BY_YEARS_CHART],
      places: [],
    },
    [UNEMPLOYMENT_RATIO]: { charts: [EMPLOYMENT_CHART], places: [] },
    [AVERAGE_ANNUAL_SPENDING]: {
      charts: [INCOME_DISTRIBUTION_CHART],
      places: [CONVENIENCE_POI_CHART],
    },
    [LIFE_EXPECTANCY]: {
      charts: [POPULATION_BY_YEARS_CHART],
      places: [],
    },
    [UNINSURED_RATIO]: { charts: [], places: [HEALTHCARE_POI_CHART] },
    [PATIENT_TO_PHYSICIAN_RATIO]: {
      charts: [],
      places: [HEALTHCARE_POI_CHART],
    },
    [UNHEALTHY_BEHAVIOUR_INDEX]: { charts: [], places: [] },
    [SPECIAL_HEALTH_CARE_NEEDS_INDEX]: {
      charts: [],
      places: [HEALTHCARE_POI_CHART],
    },
    [AIR_POLLUTION]: { charts: [AIR_QUALITY_DISTRIBUTION_CHART], places: [] },
    [BACHELORS_DEGREE_OR_HIGHER_FRACTION]: {
      charts: [EDUCATION_ATTAINMENT_CHART],
      places: [SCHOOLS_POI],
    },
    [MEDIAN_HOUSING_SALE_COST_TREND]: { charts: [VACANCY_CHART], places: [] },
    [MEDIAN_GROSS_RENT_TREND]: {
      charts: [GROSS_RENT_DISTRIBUTION_CHART],
      places: [],
    },
    [STRUCTURE_BUILT_MEDIAN_YEAR]: {
      charts: [NUMBER_OF_ROOMS_CHART],
      places: [],
    },
    [MEDIAN_GROSS_RENT]: {
      charts: [GROSS_RENT_DISTRIBUTION_CHART],
      places: [],
    },
    [MEDIAN_HOUSING_SALE_COST]: {
      charts: [NUMBER_OF_ROOMS_CHART],
      places: [],
    },
    [MEDIAN_TENURE]: { charts: [], places: [] },
    [MEDIAN_NUMBER_OF_ROOMS]: { charts: [], places: [] },
    [HOME_OWNERSHIP_RATIO]: { charts: [], places: [] },
    [HOME_PRICE_TO_INCOME_RATIO]: { charts: [], places: [] },
    [CRIME_RATE]: { charts: [], places: [] },
    [VIOLENT_CRIME_RATE]: { charts: [], places: [] },
    [PROPERTY_CRIME_RATE]: { charts: [], places: [] },
    [NUMBER_OF_VEHICLES_AVAILABLE]: {
      charts: [TIME_OF_DEPARTURE_TO_WORK_CHART],
      places: [],
    },
    [AVERAGE_TRAVEL_TIME_TO_WORK]: {
      charts: [WORK_COMMUTE_MODES_CHART],
      places: [],
    },
    [WORK_AT_HOME]: { charts: [TRAVEL_TIME_TO_WORK_CHART], places: [] },
    [CUMULATIVE_TRAFFIC_INTENSITY]: {
      charts: [TRAVEL_TIME_TO_WORK_CHART],
      places: [],
    },
    [ROAD_DENSITY]: { charts: [ROAD_DENSITY_CHART], places: [] },
    [PRESIDENTIAL_ELECTIONS_RESULTS2020]: { charts: [], places: [] },
    [HOUSE_OF_REPRESENTATIVES_ELECTION_RESULTS]: { charts: [], places: [] },
    [SENATE_ELECTION_2022_RESULTS]: { charts: [], places: [] },
    [HEALTH_INDEX_CONST]: {
      charts: [],
      places: [HEALTHCARE_POI_CHART],
    },
    [WEALTH_INDEX_CONST]: { charts: [INCOME_DISTRIBUTION_CHART], places: [] },
    [WISE_INDEX_CONST]: {
      charts: [EDUCATION_ATTAINMENT_CHART],
      places: [SCHOOLS_POI],
    },
    [COMBINED_HWW_CONST]: {
      charts: [GROSS_RENT_DISTRIBUTION_CHART],
      places: [CONVENIENCE_POI_CHART],
    },
    [BEAUTY]: { charts: [], places: [BEAUTY_POI_CHART] },
    [RETAIL]: { charts: [], places: [COMPETITORS_POI_CHART]
    }
  };

  public linkedToggles: SubscriptionToLinkedToggles = {
    unauthorized: {
      [POPULATION]: { charts: [RACE_DISTRIBUTION_CHART], places: [] },
      [MEDIAN_AGE]: { charts: [AGE_SEX_DISTRIBUTION_CHART], places: [] },
      [AVERAGE_HOUSEHOLD_SIZE]: {
        charts: [],
        places: [],
      },
      [MEDIAN_INCOME]: { charts: [INCOME_DISTRIBUTION_CHART], places: [] },
      [AVERAGE_ANNUAL_SPENDING]: {
        charts: [INCOME_DISTRIBUTION_CHART],
        places: [COMPETITORS_POI_CHART],
      },
      [LIFE_EXPECTANCY]: {
        charts: [POPULATION_BY_YEARS_CHART],
        places: [],
      },
      [UNHEALTHY_BEHAVIOUR_INDEX]: { charts: [], places: [] },
      [SPECIAL_HEALTH_CARE_NEEDS_INDEX]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [AIR_POLLUTION]: { charts: [AIR_QUALITY_DISTRIBUTION_CHART], places: [] },
      [BACHELORS_DEGREE_OR_HIGHER_FRACTION]: {
        charts: [EDUCATION_ATTAINMENT_CHART],
        places: [SCHOOLS_POI],
      },
      [STRUCTURE_BUILT_MEDIAN_YEAR]: {
        charts: [NUMBER_OF_ROOMS_CHART],
        places: [],
      },
      [MEDIAN_GROSS_RENT]: {
        charts: [GROSS_RENT_DISTRIBUTION_CHART],
        places: [],
      },
      [MEDIAN_HOUSING_SALE_COST]: {
        charts: [NUMBER_OF_ROOMS_CHART],
        places: [],
      },
      [MEDIAN_NUMBER_OF_ROOMS]: { charts: [], places: [] },
      [HOME_PRICE_TO_INCOME_RATIO]: { charts: [], places: [] },
      [CRIME_RATE]: { charts: [], places: [] },
      [NUMBER_OF_VEHICLES_AVAILABLE]: {
        charts: [TIME_OF_DEPARTURE_TO_WORK_CHART],
        places: [],
      },
      [WORK_AT_HOME]: { charts: [TRAVEL_TIME_TO_WORK_CHART], places: [] },
      [PRESIDENTIAL_ELECTIONS_RESULTS2020]: { charts: [], places: [] },
      [PRESIDENTIAL_ELECTIONS_RESULTS2024]: { charts: [], places: [] },
      [HEALTH_INDEX_CONST]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [WEALTH_INDEX_CONST]: {
        charts: [],
        places: [CONVENIENCE_POI_CHART],
      },
      [WISE_INDEX_CONST]: {
        charts: [],
        places: [],
      },
      [BEAUTY]: { charts: [], places: [BEAUTY_POI_CHART] },
    },

    trial: {
      [POPULATION]: { charts: [RACE_DISTRIBUTION_CHART], places: [] },
      [HOUSEHOLDS]: { charts: [MARITAL_STATUS_CHART], places: [] },
      [MEDIAN_AGE]: { charts: [AGE_SEX_DISTRIBUTION_CHART], places: [] },
      [AVERAGE_HOUSEHOLD_SIZE]: {
        charts: [],
        places: [],
      },
      [MEDIAN_INCOME]: { charts: [INCOME_DISTRIBUTION_CHART], places: [] },
      [MEDIAN_HOUSEHOLD_INCOME_TREND]: {
        charts: [INCOME_DISTRIBUTION_CHART, INCOME_MEDIANS_BY_YEARS_CHART],
        places: [],
      },
      [UNEMPLOYMENT_RATIO]: { charts: [EMPLOYMENT_CHART], places: [] },
      [AVERAGE_ANNUAL_SPENDING]: {
        charts: [INCOME_DISTRIBUTION_CHART],
        places: [CONVENIENCE_POI_CHART],
      },
      [LIFE_EXPECTANCY]: {
        charts: [POPULATION_BY_YEARS_CHART],
        places: [],
      },
      [UNINSURED_RATIO]: { charts: [], places: [HEALTHCARE_POI_CHART] },
      [PATIENT_TO_PHYSICIAN_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [UNHEALTHY_BEHAVIOUR_INDEX]: { charts: [], places: [] },
      [SPECIAL_HEALTH_CARE_NEEDS_INDEX]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [AIR_POLLUTION]: { charts: [AIR_QUALITY_DISTRIBUTION_CHART], places: [] },
      [BACHELORS_DEGREE_OR_HIGHER_FRACTION]: {
        charts: [EDUCATION_ATTAINMENT_CHART],
        places: [SCHOOLS_POI],
      },
      [MEDIAN_HOUSING_SALE_COST_TREND]: { charts: [VACANCY_CHART], places: [] },
      [MEDIAN_GROSS_RENT_TREND]: {
        charts: [GROSS_RENT_DISTRIBUTION_CHART],
        places: [],
      },
      [STRUCTURE_BUILT_MEDIAN_YEAR]: {
        charts: [NUMBER_OF_ROOMS_CHART],
        places: [],
      },
      [MEDIAN_GROSS_RENT]: {
        charts: [GROSS_RENT_DISTRIBUTION_CHART],
        places: [],
      },
      [MEDIAN_HOUSING_SALE_COST]: {
        charts: [NUMBER_OF_ROOMS_CHART],
        places: [],
      },
      [MEDIAN_TENURE]: { charts: [], places: [] },
      [MEDIAN_NUMBER_OF_ROOMS]: { charts: [], places: [] },
      [HOME_OWNERSHIP_RATIO]: { charts: [], places: [] },
      [HOME_PRICE_TO_INCOME_RATIO]: { charts: [], places: [] },
      [CRIME_RATE]: { charts: [], places: [] },
      [VIOLENT_CRIME_RATE]: { charts: [], places: [] },
      [PROPERTY_CRIME_RATE]: { charts: [], places: [] },
      [NUMBER_OF_VEHICLES_AVAILABLE]: {
        charts: [TIME_OF_DEPARTURE_TO_WORK_CHART],
        places: [],
      },
      [AVERAGE_TRAVEL_TIME_TO_WORK]: {
        charts: [WORK_COMMUTE_MODES_CHART],
        places: [],
      },
      [WORK_AT_HOME]: { charts: [TRAVEL_TIME_TO_WORK_CHART], places: [] },
      [CUMULATIVE_TRAFFIC_INTENSITY]: {
        charts: [TRAVEL_TIME_TO_WORK_CHART],
        places: [],
      },
      [ROAD_DENSITY]: { charts: [ROAD_DENSITY_CHART], places: [] },
      [PRESIDENTIAL_ELECTIONS_RESULTS2020]: { charts: [], places: [] },
      [HOUSE_OF_REPRESENTATIVES_ELECTION_RESULTS]: { charts: [], places: [] },
      [SENATE_ELECTION_2022_RESULTS]: { charts: [], places: [] },
      [HEALTH_INDEX_CONST]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [WEALTH_INDEX_CONST]: { charts: [], places: [] },
      [WISE_INDEX_CONST]: {
        charts: [],
        places: [],
      },
      [COMBINED_HWW_CONST]: {
        charts: [],
        places: [CONVENIENCE_POI_CHART],
      },
      [BEAUTY]: { charts: [], places: [BEAUTY_POI_CHART] },
      [RETAIL]: { charts: [], places: [COMPETITORS_POI_CHART] },
    },

    [SubscriptionPlans.BASIC]: {
      ...this.all
    },

    [SubscriptionPlans.PLUS]: {
      ...this.all
    },
    [SubscriptionPlans.PRO]: {
      ...this.all
    },

    [SubscriptionPlans.FULL_ACCESS]: {
      [POPULATION]: { charts: [RACE_DISTRIBUTION_CHART], places: [] },
      [HOUSEHOLDS]: { charts: [MARITAL_STATUS_CHART], places: [] },
      [MEDIAN_AGE]: { charts: [AGE_SEX_DISTRIBUTION_CHART], places: [] },
      [AVERAGE_HOUSEHOLD_SIZE]: {
        charts: [],
        places: [],
      },
      [MEDIAN_INCOME]: { charts: [INCOME_DISTRIBUTION_CHART], places: [] },
      [MEDIAN_HOUSEHOLD_INCOME_TREND]: {
        charts: [INCOME_DISTRIBUTION_CHART, INCOME_MEDIANS_BY_YEARS_CHART],
        places: [],
      },
      [UNEMPLOYMENT_RATIO]: { charts: [EMPLOYMENT_CHART], places: [] },
      [AVERAGE_ANNUAL_SPENDING]: {
        charts: [INCOME_DISTRIBUTION_CHART],
        places: [CONVENIENCE_POI_CHART],
      },
      [LIFE_EXPECTANCY]: {
        charts: [POPULATION_BY_YEARS_CHART],
        places: [],
      },
      [UNINSURED_RATIO]: { charts: [], places: [HEALTHCARE_POI_CHART] },
      [PATIENT_TO_PHYSICIAN_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [UNHEALTHY_BEHAVIOUR_INDEX]: { charts: [], places: [] },
      [SPECIAL_HEALTH_CARE_NEEDS_INDEX]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [AIR_POLLUTION]: { charts: [AIR_QUALITY_DISTRIBUTION_CHART], places: [] },
      [BACHELORS_DEGREE_OR_HIGHER_FRACTION]: {
        charts: [EDUCATION_ATTAINMENT_CHART],
        places: [SCHOOLS_POI],
      },
      [MEDIAN_HOUSING_SALE_COST_TREND]: { charts: [VACANCY_CHART], places: [] },
      [MEDIAN_GROSS_RENT_TREND]: {
        charts: [GROSS_RENT_DISTRIBUTION_CHART],
        places: [],
      },
      [STRUCTURE_BUILT_MEDIAN_YEAR]: {
        charts: [NUMBER_OF_ROOMS_CHART],
        places: [],
      },
      [MEDIAN_GROSS_RENT]: {
        charts: [GROSS_RENT_DISTRIBUTION_CHART],
        places: [],
      },
      [MEDIAN_HOUSING_SALE_COST]: {
        charts: [NUMBER_OF_ROOMS_CHART],
        places: [],
      },
      [MEDIAN_TENURE]: { charts: [], places: [] },
      [MEDIAN_NUMBER_OF_ROOMS]: { charts: [], places: [] },
      [HOME_OWNERSHIP_RATIO]: { charts: [], places: [] },
      [HOME_PRICE_TO_INCOME_RATIO]: { charts: [], places: [] },
      [CRIME_RATE]: { charts: [], places: [] },
      [VIOLENT_CRIME_RATE]: { charts: [], places: [] },
      [PROPERTY_CRIME_RATE]: { charts: [], places: [] },
      [NUMBER_OF_VEHICLES_AVAILABLE]: {
        charts: [TIME_OF_DEPARTURE_TO_WORK_CHART],
        places: [],
      },
      [AVERAGE_TRAVEL_TIME_TO_WORK]: {
        charts: [WORK_COMMUTE_MODES_CHART],
        places: [],
      },
      [WORK_AT_HOME]: { charts: [TRAVEL_TIME_TO_WORK_CHART], places: [] },
      [CUMULATIVE_TRAFFIC_INTENSITY]: {
        charts: [TRAVEL_TIME_TO_WORK_CHART],
        places: [],
      },
      [ROAD_DENSITY]: { charts: [ROAD_DENSITY_CHART], places: [] },
      [PRESIDENTIAL_ELECTIONS_RESULTS2020]: { charts: [], places: [] },
      [HOUSE_OF_REPRESENTATIVES_ELECTION_RESULTS]: { charts: [], places: [] },
      [SENATE_ELECTION_2022_RESULTS]: { charts: [], places: [] },
      [HEALTH_INDEX_CONST]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [WEALTH_INDEX_CONST]: { charts: [INCOME_DISTRIBUTION_CHART], places: [] },
      [WISE_INDEX_CONST]: {
        charts: [EDUCATION_ATTAINMENT_CHART],
        places: [SCHOOLS_POI],
      },
      [COMBINED_HWW_CONST]: {
        charts: [GROSS_RENT_DISTRIBUTION_CHART],
        places: [CONVENIENCE_POI_CHART],
      },
      [BEAUTY]: { charts: [], places: [BEAUTY_POI_CHART] },
      [RETAIL]: { charts: [], places: [COMPETITORS_POI_CHART] },
    },

    [SubscriptionPlans.KNOW_YOUR_COUNTRY]: {
      [POPULATION]: { charts: [AGE_SEX_DISTRIBUTION_CHART], places: [] },
      [POPULATION_GROWTH_TREND]: {
        charts: [POPULATION_BY_YEARS_CHART],
        places: [],
      },
      [PEOPLE_PER_MI2]: { charts: [RACE_DISTRIBUTION_CHART], places: [] },
      [HOUSEHOLDS]: { charts: [MARITAL_STATUS_CHART], places: [] },
      [MEDIAN_AGE]: { charts: [], places: [] },
      [AVERAGE_HOUSEHOLD_SIZE]: { charts: [], places: [] },
      [MEDIAN_INCOME]: { charts: [INCOME_DISTRIBUTION_CHART], places: [] },
      [MEDIAN_HOUSEHOLD_INCOME_TREND]: { charts: [], places: [] },
      [UNEMPLOYMENT_RATIO]: { charts: [EMPLOYMENT_CHART], places: [] },
      [GENDER_PAY_GAP]: { charts: [], places: [] },
      [AVERAGE_ANNUAL_SPENDING]: {
        charts: [INCOME_DISTRIBUTION_CHART],
        places: [],
      },
      [LIFE_EXPECTANCY]: { charts: [], places: [] },
      [UNINSURED_RATIO]: { charts: [], places: [HEALTHCARE_POI_CHART] },
      [PATIENT_TO_PHYSICIAN_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [PATIENT_TO_DENTIST_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [PATIENT_TO_THERAPIST_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [SPECIAL_HEALTH_CARE_NEEDS_INDEX]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [AIR_POLLUTION]: { charts: [AIR_QUALITY_DISTRIBUTION_CHART], places: [] },
      [BACHELORS_DEGREE_OR_HIGHER_FRACTION]: {
        charts: [EDUCATION_ATTAINMENT_CHART],
        places: [SCHOOLS_POI],
      },
      [MEDIAN_HOUSING_SALE_COST_TREND]: { charts: [VACANCY_CHART], places: [] },
      [MEDIAN_GROSS_RENT_TREND]: {
        charts: [GROSS_RENT_DISTRIBUTION_CHART],
        places: [],
      },
      [STRUCTURE_BUILT_MEDIAN_YEAR]: {
        charts: [NUMBER_OF_ROOMS_CHART],
        places: [],
      },
      [MEDIAN_GROSS_RENT]: {
        charts: [GROSS_RENT_DISTRIBUTION_CHART],
        places: [],
      },
      [MEDIAN_HOUSING_SALE_COST]: {
        charts: [NUMBER_OF_ROOMS_CHART],
        places: [],
      },
      [MEDIAN_TENURE]: { charts: [], places: [] },
      [MEDIAN_NUMBER_OF_ROOMS]: { charts: [], places: [] },
      [HOME_OWNERSHIP_RATIO]: { charts: [], places: [] },
      [HOME_PRICE_TO_INCOME_RATIO]: { charts: [], places: [] },
      [CRIME_RATE]: { charts: [], places: [] },
      [VIOLENT_CRIME_RATE]: { charts: [], places: [] },
      [PROPERTY_CRIME_RATE]: { charts: [], places: [] },
      [NUMBER_OF_VEHICLES_AVAILABLE]: {
        charts: [HOUSEHOLDS_BY_VEHICLES_CHART],
        places: [],
      },
      [AVERAGE_TRAVEL_TIME_TO_WORK]: {
        charts: [TIME_OF_DEPARTURE_TO_WORK_CHART],
        places: [],
      },
      [WORK_AT_HOME]: { charts: [WORK_COMMUTE_MODES_CHART], places: [] },
      [CUMULATIVE_TRAFFIC_INTENSITY]: {
        charts: [TRAVEL_TIME_TO_WORK_CHART],
        places: [],
      },
      [ROAD_DENSITY]: { charts: [ROAD_DENSITY_CHART], places: [] },
      [PRESIDENTIAL_ELECTIONS_RESULTS2020]: { charts: [], places: [] },
      [PRESIDENTIAL_ELECTIONS_RESULTS2024]: { charts: [], places: [] },
      [HOUSE_OF_REPRESENTATIVES_ELECTION_RESULTS]: { charts: [], places: [] },
      [SENATE_ELECTION_2022_RESULTS]: { charts: [], places: [] },
      [HEALTH_INDEX_CONST]: { charts: [], places: [HEALTHCARE_POI_CHART] },
      [WEALTH_INDEX_CONST]: { charts: [], places: [CONVENIENCE_POI_CHART] },
      [WISE_INDEX_CONST]: { charts: [], places: [] },
      [COMBINED_HWW_CONST]: { charts: [], places: [] },
    },
    [SubscriptionPlans.COMMUNITY_COMPASS]: {
      [POPULATION_GROWTH_TREND]: { charts: [], places: [] },
      [PEOPLE_PER_MI2]: { charts: [RACE_DISTRIBUTION_CHART], places: [] },
      [MEDIAN_AGE]: { charts: [AGE_SEX_DISTRIBUTION_CHART], places: [] },
      [AVERAGE_HOUSEHOLD_SIZE]: {
        charts: [HOUSEHOLDS_BY_VEHICLES_CHART],
        places: [],
      },
      [MEDIAN_INCOME]: { charts: [INCOME_DISTRIBUTION_CHART], places: [] },
      [MEDIAN_HOUSEHOLD_INCOME_TREND]: {
        charts: [INCOME_DISTRIBUTION_CHART, INCOME_MEDIANS_BY_YEARS_CHART],
        places: [],
      },
      [UNEMPLOYMENT_RATIO]: { charts: [EMPLOYMENT_CHART], places: [] },
      [AVERAGE_ANNUAL_SPENDING]: {
        charts: [INCOME_DISTRIBUTION_CHART],
        places: [CONVENIENCE_POI_CHART],
      },
      [LIFE_EXPECTANCY]: { charts: [], places: [] },
      [PATIENT_TO_PHYSICIAN_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [PATIENT_TO_DENTIST_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [PATIENT_TO_THERAPIST_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [UNHEALTHY_BEHAVIOUR_INDEX]: { charts: [], places: [] },
      [SPECIAL_HEALTH_CARE_NEEDS_INDEX]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [AIR_POLLUTION]: { charts: [AIR_QUALITY_DISTRIBUTION_CHART], places: [] },
      [BACHELORS_DEGREE_OR_HIGHER_FRACTION]: {
        charts: [EDUCATION_ATTAINMENT_CHART],
        places: [SCHOOLS_POI],
      },
      [MEDIAN_HOUSING_SALE_COST_TREND]: { charts: [VACANCY_CHART], places: [] },
      [MEDIAN_GROSS_RENT_TREND]: {
        charts: [GROSS_RENT_DISTRIBUTION_CHART],
        places: [],
      },
      [STRUCTURE_BUILT_MEDIAN_YEAR]: {
        charts: [NUMBER_OF_ROOMS_CHART],
        places: [],
      },
      [MEDIAN_GROSS_RENT]: {
        charts: [GROSS_RENT_DISTRIBUTION_CHART],
        places: [],
      },
      [MEDIAN_HOUSING_SALE_COST]: {
        charts: [NUMBER_OF_ROOMS_CHART],
        places: [],
      },
      [MEDIAN_TENURE]: { charts: [], places: [] },
      [MEDIAN_NUMBER_OF_ROOMS]: { charts: [], places: [] },
      [HOME_OWNERSHIP_RATIO]: { charts: [], places: [] },
      [HOME_PRICE_TO_INCOME_RATIO]: { charts: [], places: [] },
      [CRIME_RATE]: { charts: [], places: [] },
      [VIOLENT_CRIME_RATE]: { charts: [], places: [] },
      [PROPERTY_CRIME_RATE]: { charts: [], places: [] },
      [AVERAGE_TRAVEL_TIME_TO_WORK]: {
        charts: [WORK_COMMUTE_MODES_CHART],
        places: [],
      },
      [HEALTH_INDEX_CONST]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [WEALTH_INDEX_CONST]: {
        charts: [],
        places: [CONVENIENCE_POI_CHART],
      },
      [WISE_INDEX_CONST]: {
        charts: [],
        places: [],
      },
      [COMBINED_HWW_CONST]: { charts: [], places: [] },
    },

    [SubscriptionPlans.REALTOR]: {
      [POPULATION_GROWTH_TREND]: { charts: [], places: [] },
      [PEOPLE_PER_MI2]: { charts: [], places: [] },
      [MEDIAN_AGE]: { charts: [AGE_SEX_DISTRIBUTION_CHART], places: [] },
      [AVERAGE_HOUSEHOLD_SIZE]: {
        charts: [HOUSEHOLDS_BY_VEHICLES_CHART],
        places: [],
      },
      [MEDIAN_INCOME]: { charts: [INCOME_DISTRIBUTION_CHART], places: [] },
      [MEDIAN_HOUSEHOLD_INCOME_TREND]: { charts: [], places: [] },
      [UNEMPLOYMENT_RATIO]: { charts: [], places: [] },
      [AVERAGE_ANNUAL_SPENDING]: {
        charts: [],
        places: [CONVENIENCE_POI_CHART],
      },
      [LIFE_EXPECTANCY]: { charts: [], places: [] },
      [PATIENT_TO_PHYSICIAN_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [PATIENT_TO_DENTIST_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [PATIENT_TO_THERAPIST_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [UNHEALTHY_BEHAVIOUR_INDEX]: { charts: [], places: [] },
      [SPECIAL_HEALTH_CARE_NEEDS_INDEX]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [AIR_POLLUTION]: { charts: [AIR_QUALITY_DISTRIBUTION_CHART], places: [] },
      [BACHELORS_DEGREE_OR_HIGHER_FRACTION]: {
        charts: [EDUCATION_ATTAINMENT_CHART],
        places: [SCHOOLS_POI],
      },
      [MEDIAN_HOUSING_SALE_COST_TREND]: { charts: [VACANCY_CHART], places: [] },
      [MEDIAN_GROSS_RENT_TREND]: {
        charts: [GROSS_RENT_DISTRIBUTION_CHART],
        places: [],
      },
      [STRUCTURE_BUILT_MEDIAN_YEAR]: {
        charts: [NUMBER_OF_ROOMS_CHART],
        places: [],
      },
      [MEDIAN_GROSS_RENT]: {
        charts: [GROSS_RENT_DISTRIBUTION_CHART],
        places: [],
      },
      [MEDIAN_HOUSING_SALE_COST]: {
        charts: [NUMBER_OF_ROOMS_CHART],
        places: [],
      },
      [MEDIAN_TENURE]: { charts: [], places: [] },
      [MEDIAN_NUMBER_OF_ROOMS]: { charts: [], places: [] },
      [HOME_OWNERSHIP_RATIO]: { charts: [], places: [] },
      [HOME_PRICE_TO_INCOME_RATIO]: { charts: [], places: [] },
      [CRIME_RATE]: { charts: [], places: [] },
      [VIOLENT_CRIME_RATE]: { charts: [], places: [] },
      [PROPERTY_CRIME_RATE]: { charts: [], places: [] },
      [AVERAGE_TRAVEL_TIME_TO_WORK]: {
        charts: [WORK_COMMUTE_MODES_CHART],
        places: [],
      },
      [HEALTH_INDEX_CONST]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [WEALTH_INDEX_CONST]: { charts: [], places: [CONVENIENCE_POI_CHART] },
      [WISE_INDEX_CONST]: {
        charts: [],
        places: [],
      },
      [COMBINED_HWW_CONST]: { charts: [], places: [] },
    },

    [SubscriptionPlans.ENTERPRISE]: {
      [POPULATION]: { charts: [AGE_SEX_DISTRIBUTION_CHART], places: [] },
      [POPULATION_GROWTH_TREND]: {
        charts: [POPULATION_BY_YEARS_CHART],
        places: [],
      },
      [PEOPLE_PER_MI2]: { charts: [RACE_DISTRIBUTION_CHART], places: [] },
      [HOUSEHOLDS]: { charts: [MARITAL_STATUS_CHART], places: [] },
      [MEDIAN_AGE]: { charts: [], places: [] },
      [AVERAGE_HOUSEHOLD_SIZE]: {
        charts: [],
        places: [],
      },
      [MEDIAN_INCOME]: { charts: [INCOME_DISTRIBUTION_CHART], places: [] },
      [MEDIAN_HOUSEHOLD_INCOME_TREND]: { charts: [], places: [] },
      [UNEMPLOYMENT_RATIO]: { charts: [EMPLOYMENT_CHART], places: [] },
      [AVERAGE_ANNUAL_SPENDING]: {
        charts: [INCOME_DISTRIBUTION_CHART],
        places: [],
      },
      [UNINSURED_RATIO]: { charts: [], places: [HEALTHCARE_POI_CHART] },
      [PATIENT_TO_PHYSICIAN_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [PATIENT_TO_DENTIST_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [PATIENT_TO_THERAPIST_RATIO]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [UNHEALTHY_BEHAVIOUR_INDEX]: { charts: [], places: [] },
      [SPECIAL_HEALTH_CARE_NEEDS_INDEX]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [BACHELORS_DEGREE_OR_HIGHER_FRACTION]: {
        charts: [EDUCATION_ATTAINMENT_CHART],
        places: [],
      },
      [MEDIAN_HOUSING_SALE_COST_TREND]: { charts: [], places: [] },
      [MEDIAN_GROSS_RENT_TREND]: { charts: [], places: [] },
      [MEDIAN_GROSS_RENT]: { charts: [], places: [] },
      [MEDIAN_HOUSING_SALE_COST]: { charts: [], places: [] },
      [HOME_PRICE_TO_INCOME_RATIO]: { charts: [], places: [] },
      [CRIME_RATE]: { charts: [], places: [] },
      [VIOLENT_CRIME_RATE]: { charts: [], places: [] },
      [PROPERTY_CRIME_RATE]: { charts: [], places: [] },
      [NUMBER_OF_VEHICLES_AVAILABLE]: {
        charts: [HOUSEHOLDS_BY_VEHICLES_CHART],
        places: [],
      },
      [AVERAGE_TRAVEL_TIME_TO_WORK]: {
        charts: [TIME_OF_DEPARTURE_TO_WORK_CHART],
        places: [],
      },
      [WORK_AT_HOME]: { charts: [WORK_COMMUTE_MODES_CHART], places: [] },
      [CUMULATIVE_TRAFFIC_INTENSITY]: {
        charts: [TRAVEL_TIME_TO_WORK_CHART],
        places: [],
      },
      [ROAD_DENSITY]: { charts: [ROAD_DENSITY_CHART], places: [] },
      [CONVENIENCE_STORES]: {
        charts: [],
        places: [COMPETITORS_POI_CHART],
      },
      [DRUGSTORES]: {
        charts: [],
        places: [HEALTHCARE_POI_CHART],
      },
      [BEAUTY]: {
        charts: [],
        places: [BEAUTY_POI_CHART],
      },
      [RETAIL]: {
        charts: [],
        places: [COMPETITORS_POI_CHART],
      },
    },
  };

  private groupNameToStatusMap = new Map<string, boolean>();

  constructor(
    private ageSexService: PopulationService,
    private targetAudienceService: TargetAudienceService,
    private incomeDistributionService: IncomeDistributionService,
    private homeownershipService: HomeownershipService,
    private educationService: EducationService,
    private commuteAndMobilityService: CommuteAndMobilityService,
    private healthService: HealthService,
    private POIsService: PoisService,
    private politicalLandscapeService: PoliticalLandscapeService,
    private mapColoring: MapColoringService,
    private layerStore: LayerStoreService,
    private layersMenuService: LayersMenuService,
    private filtersService: FiltersMenuService
  ) {
    this.layerStore.activeLayer.subscribe((layer) => {
      this.handleMoveEnd();
    });
  }

  public eventDispatcher(
    eventType: string,
    feature?: MapboxGeoJSONFeature,
    layer?: string
  ): void {
    switch (eventType) {
      case 'click':
        this.handleClick(feature!);
        break;
      case 'moveend':
        this.handleMoveEnd(layer);
        break;

      default:
        throw new Error(
          'Implement switch for ' + eventType + ', layers-data.service.ts'
        );
    }
  }

  private handleClick(feature: MapboxGeoJSONFeature): void {
    const isGroupActive = (groupTitle: string): boolean => {
      // Check toggleMap for active chart toggles
      const hasActiveToggle = Array.from(this.toggleMap.entries()).some(
        ([key, control]) =>
          this.getGroupNameByFeatureConst(key) === groupTitle && control.value
      );

      // Check placesToggleMap for active POI toggles
      const hasActivePlace = Array.from(this.placesToggleMap.entries()).some(
        ([key, control]) =>
          this.getGroupNameByFeatureConst(key) === groupTitle && control.value
      );

      const mapColor = this.layersMenuService.groupsData
        .filter((group) => {
          return group.title === groupTitle && group.data.mapColor !== null;
        })
        .map((group) => group.data.mapColor)
        .flat(1);

      const hasActiveMap = mapColor.some((mapColor) => {
        return mapColor!.featureConst === this.layerStore.activeLayer.value;
      });

      return hasActiveToggle || hasActivePlace || hasActiveMap;
    };

    if (isGroupActive(POI_GROUP_TITLE)) {
      this.POIsService.fillChartsData(feature);
    }

    if (isGroupActive(POPULATION_GROUP_TITLE)) {
      this.ageSexService.fillChartsData(feature);
    }
    if (isGroupActive(TARGET_AUDIENCE_GROUP_TITLE)) {
      this.targetAudienceService.fillChartsData(feature);
    }

    if (isGroupActive(WEALTH_GROUP_TITLE)) {
      this.incomeDistributionService.fillChartsData(feature);
    }

    if (isGroupActive(HOUSING_GROUP_TITLE)) {
      this.homeownershipService.fillChartsData(feature);
    }

    if (isGroupActive(EDUCATION_GROUP_TITLE)) {
      this.educationService.fillChartsData(feature);
    }

    if (isGroupActive(HEALTH_GROUP_TITLE)) {
      this.healthService.fillChartsData(feature);
    }

    if (isGroupActive(COMMUTE_AND_MOBILITY_GROUP_TITLE)) {
      this.commuteAndMobilityService.fillChartsData(feature);
    }
  }

  public handleOpenedGroup(group: string, status: boolean): void {
    this.groupNameToStatusMap.set(group, status);
  }

  public getIsGroupOpened(group: string): boolean {
    return this.groupNameToStatusMap.get(group) || false;
  }

  private handleMoveEnd(layer?: string): void {
    const currentLayer = layer ?? this.layerStore.activeLayer.getValue();

    if (this.filtersService.activeFilters.length > 0) {
      this.filtersService.activeFilters.forEach((filter) => {
        this.handleLayersGroups(filter.featureConst);
      });
    }

    this.handleLayersGroups(currentLayer);
  }

  private handleLayersGroups(currentLayer: string): void {
    if (
      this.groupNameToStatusMap.get(POLITICAL_LANDSCAPE_GROUP_TITLE) ||
      voteDataFeatures.includes(currentLayer)
    ) {
      this.politicalLandscapeService.setVotersData(currentLayer);
    }

    if (
      this.groupNameToStatusMap.get(POI_GROUP_TITLE) &&
      this.layerStore.isCurrentLevelCells()
    ) {
      this.POIsService.handlePoisOnMap();
    }

    if (
      this.layerStore.activeLayer.getValue() === TARGET_AUDIENCE ||
      targetAudienceIndustries.includes(currentLayer)
    ) {
      this.targetAudienceService.colorMapByIndustry(currentLayer);
    }

    if (availableInBulkFeatures.includes(currentLayer)) {
      this.mapColoring.colorMapByCells(currentLayer);
    }
  }

  public getGroupNameByFeatureConst(featureConst: string): string {
    for (const group of this.layersMenuService.groupsData) {
      const { mapColor, charts, places } = group.data;

      if (mapColor?.some((f) => f.featureConst === featureConst)) {
        return group.title;
      }

      if (charts?.some((c) => c.featureConst === featureConst)) {
        return group.title;
      }

      if (places?.some((p) => p.featureConst === featureConst)) {
        return group.title;
      }
    }

    return '';
  }
}
